import React, { useState, useEffect, useRef } from 'react';
import { Save, Trash2, Plus, X, Globe, Lightbulb, Hash, Target, Sparkles, Download, RefreshCw, CheckCircle2, AlertCircle, Wifi, WifiOff, Settings2, Pencil, Check, XCircle, AlertTriangle, StopCircle, Zap, Brain, FileSearch, Ban } from 'lucide-react';

import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Loading } from '../ui/Loading';
import { Modal } from '../ui/Modal';
import type { Project } from '../../types';
import { webhookService, projectService, apiCancelTokens } from '../../services/api';

interface ProjectSettingsProps {
    project: Project;
    onUpdate: (data: Partial<Project>) => Promise<void>;
    onDelete: (projectId: string) => Promise<void>;
}

export const ProjectSettings: React.FC<ProjectSettingsProps> = ({
    project,
    onUpdate,
    onDelete
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        url: '',
        username: '',
        password: ''
    });

    const [faqConfig, setFaqConfig] = useState<{ format: string; template: string | null; detected_at: string | null } | null>(null);
    const [isDetectingFaq, setIsDetectingFaq] = useState(false);
    const [faqDetectionError, setFaqDetectionError] = useState<string | null>(null);

    const [availableTaxonomies, setAvailableTaxonomies] = useState<any[]>([]);
    const [taxonomiesConfig, setTaxonomiesConfig] = useState<any[]>([]);
    const [isDetectingTaxonomies, setIsDetectingTaxonomies] = useState(false);
    const [taxonomiesError, setTaxonomiesError] = useState<string | null>(null);

    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [connectionMessage, setConnectionMessage] = useState('');

    // Estado para dominios excluidos (competidores)
    const [excludedDomains, setExcludedDomains] = useState<{ id: string; url: string }[]>([]);
    const [newExcludedDomain, setNewExcludedDomain] = useState('');

    const MASKED_PASSWORD = '••••••••';

    useEffect(() => {
        if (project) {
            setFormData({
                name: project.name || '',
                description: project.description || '',
                url: (project as any).wordpress_url || '',
                username: (project as any).wordpress_username || '',
                password: MASKED_PASSWORD
            });
            loadFaqConfig();
            loadTaxonomiesConfig();
            loadExcludedDomains();
        }
    }, [project]);

    const loadExcludedDomains = async () => {
        if (!project?.id) return;
        try {
            const domains = await webhookService.getProjectCompetitors(project.id);
            setExcludedDomains(domains);
        } catch (error) {
            // Error loading excluded domains
        }
    };

    const handleAddExcludedDomain = async () => {
        if (!newExcludedDomain.trim()) return;
        try {
            // Limpiar el dominio (quitar protocolo y www si existe)
            let domain = newExcludedDomain.trim().toLowerCase();
            domain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];

            await webhookService.addProjectCompetitors(project.id, [domain]);
            await loadExcludedDomains();
            setNewExcludedDomain('');
        } catch (error) {
            // Error adding domain
        }
    };

    const handleDeleteExcludedDomain = async (url: string) => {
        try {
            await webhookService.deleteProjectCompetitor(project.id, url);
            await loadExcludedDomains();
        } catch (error) {
            // Error deleting domain
        }
    };

    const loadFaqConfig = async () => {
        if (!project?.id) return;
        try {
            const config = await projectService.getFaqConfig(project.id);
            setFaqConfig(config);
        } catch (error) {
            // Error loading FAQ config
        }
    };

    const handleDetectFaq = async () => {
        if (!project?.id) return;
        setIsDetectingFaq(true);
        setFaqDetectionError(null);
        try {
            const result = await projectService.detectFaqFormat(project.id);
            if (result.success) {
                setFaqConfig({
                    format: result.format,
                    template: result.template,
                    detected_at: new Date().toISOString()
                });
            } else {
                setFaqDetectionError(result.message || 'No se pudo detectar el formato FAQ');
            }
        } catch (error: any) {
            setFaqDetectionError(error.message || 'Error al detectar formato FAQ');
        } finally {
            setIsDetectingFaq(false);
        }
    };

    const loadTaxonomiesConfig = () => {
        if (!project?.id) return;
        try {
            let config = (project as any).taxonomies_config || [];
            console.log('Loading taxonomies config:', config); // Debug

            // Parse base64 format if needed
            if (typeof config === 'string' && config.startsWith('base64:type')) {
                try {
                    const base64Match = config.match(/^base64:type\d+:(.+)$/);
                    if (base64Match) {
                        const decoded = atob(base64Match[1]);
                        config = JSON.parse(decoded);
                        console.log('Decoded taxonomies:', config);
                    }
                } catch (err) {
                    console.error('Error decoding base64:', err);
                    config = [];
                }
            }

            // Ensure it's an array
            if (!Array.isArray(config)) {
                config = [];
            }

            setTaxonomiesConfig(config);
            // Si hay configuración guardada, también poblar availableTaxonomies
            if (config.length > 0) {
                setAvailableTaxonomies(config);
            }
        } catch (error) {
            console.error('Error loading taxonomies config:', error);
        }
    };

    const handleDetectTaxonomies = async () => {
        if (!project?.id) return;
        setIsDetectingTaxonomies(true);
        setTaxonomiesError(null);
        try {
            const taxonomies = await projectService.getWordPressTaxonomies(project.id);
            setAvailableTaxonomies(taxonomies);

            // Si no hay configuración previa, habilitar taxonomías comunes por defecto
            if (taxonomiesConfig.length === 0) {
                const defaultConfig = taxonomies.map((tax: any) => ({
                    slug: tax.slug,
                    enabled: ['category', 'post_tag'].includes(tax.slug), // Habilitar categories y tags por defecto
                    label: tax.label,
                    rest_base: tax.rest_base
                }));
                setTaxonomiesConfig(defaultConfig);
            } else {
                // Actualizar configuración existente con nuevas taxonomías detectadas
                const existingSlugs = new Set(taxonomiesConfig.map((t: any) => t.slug));
                const newTaxonomies = taxonomies
                    .filter((tax: any) => !existingSlugs.has(tax.slug))
                    .map((tax: any) => ({
                        slug: tax.slug,
                        enabled: false,
                        label: tax.label,
                        rest_base: tax.rest_base
                    }));
                setTaxonomiesConfig([...taxonomiesConfig, ...newTaxonomies]);
            }
        } catch (error: any) {
            setTaxonomiesError(error.message || 'Error al detectar taxonomías');
        } finally {
            setIsDetectingTaxonomies(false);
        }
    };

    const toggleTaxonomy = (slug: string) => {
        setTaxonomiesConfig(prev =>
            prev.map(tax =>
                tax.slug === slug ? { ...tax, enabled: !tax.enabled } : tax
            )
        );
    };

    const handleTestConnection = async () => {
        if (!formData.url || !formData.username) {
            setConnectionStatus('error');
            setConnectionMessage('Se requiere URL y usuario');
            return;
        }
        setIsTestingConnection(true);
        setConnectionStatus('idle');
        setConnectionMessage('');
        try {
            const response = await fetch('/api/wordpress/test-connection', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    url: formData.url.replace(/\/$/, ''),
                    username: formData.username,
                    password: formData.password,
                    projectId: project.id
                })
            });
            const data = await response.json();
            if (data.success) {
                setConnectionStatus('success');
                setConnectionMessage('Conexión OK');
            } else {
                setConnectionStatus('error');
                setConnectionMessage(data.error || 'Error de conexión');
            }
        } catch (error: any) {
            setConnectionStatus('error');
            setConnectionMessage(error.message || 'Error');
        } finally {
            setIsTestingConnection(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload: any = {
                name: formData.name,
                description: formData.description,
                wordpress_url: formData.url.replace(/\/$/, ''),
                wordpress_username: formData.username
            };
            if (formData.password && formData.password !== MASKED_PASSWORD) {
                payload.wordpress_password = formData.password;
            }
            // Agregar configuración de taxonomías (siempre, incluso si está vacío para limpiar)
            payload.taxonomies_config = taxonomiesConfig;
            console.log('Saving taxonomies config:', taxonomiesConfig); // Debug
            await onUpdate(payload);
            console.log('Taxonomies saved successfully'); // Debug
        } catch (error) {
            // Error updating
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        setIsLoading(true);
        try {
            await onDelete(project.id);
        } catch (error) {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-8 px-4">
            <form onSubmit={handleSubmit} className="space-y-6">

                {/* Project Details */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900">Detalles</h3>
                    </div>
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Nombre</label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                required
                                className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Descripción</label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                rows={3}
                                className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm resize-none"
                            />
                        </div>
                    </div>
                </div>

                {/* WordPress */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900">WordPress</h3>
                    </div>
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1.5">URL del sitio</label>
                            <Input
                                value={formData.url}
                                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                                onBlur={() => setFormData(prev => ({ ...prev, url: prev.url.replace(/\/$/, '') }))}
                                placeholder="https://tusitio.com"
                                className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Usuario</label>
                                <Input
                                    value={formData.username}
                                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                    className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Contraseña app</label>
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                    className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleTestConnection}
                                disabled={isTestingConnection || !formData.url || !formData.username}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                    connectionStatus === 'success'
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : connectionStatus === 'error'
                                        ? 'bg-red-50 text-red-700'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {isTestingConnection ? (
                                    <><Loading size="sm" /> Probando</>
                                ) : connectionStatus === 'success' ? (
                                    <><Wifi className="w-4 h-4" /> Conectado</>
                                ) : connectionStatus === 'error' ? (
                                    <><WifiOff className="w-4 h-4" /> Error</>
                                ) : (
                                    <><Wifi className="w-4 h-4" /> Probar</>
                                )}
                            </button>
                            {connectionMessage && (
                                <span className={`text-xs ${connectionStatus === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {connectionMessage}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* FAQ Detection - Only if WordPress configured */}
                {formData.url && formData.username && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-900">Formato FAQ</h3>
                        </div>
                        <div className="p-5">
                            {faqConfig && faqConfig.format !== 'unknown' ? (
                                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                        <div>
                                            <span className="text-sm font-medium text-emerald-800">{faqConfig.format.toUpperCase()}</span>
                                            {faqConfig.detected_at && (
                                                <p className="text-xs text-emerald-600">
                                                    {new Date(faqConfig.detected_at).toLocaleDateString('es-ES')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleDetectFaq}
                                        disabled={isDetectingFaq}
                                        className="p-2 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${isDetectingFaq ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-sm text-gray-600">No detectado</span>
                                    <button
                                        type="button"
                                        onClick={handleDetectFaq}
                                        disabled={isDetectingFaq}
                                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {isDetectingFaq ? <Loading size="sm" /> : <Sparkles className="w-4 h-4" />}
                                        Detectar
                                    </button>
                                </div>
                            )}
                            {faqDetectionError && (
                                <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                                    <AlertCircle className="w-3.5 h-3.5" />
                                    {faqDetectionError}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Taxonomies Configuration - Only if WordPress configured */}
                {formData.url && formData.username && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-900">Taxonomías para Enlaces Internos</h3>
                            <p className="text-xs text-gray-500 mt-1">Configura qué taxonomías usar en enlaces internos</p>
                        </div>
                        <div className="p-5 space-y-3">
                            {availableTaxonomies.length === 0 ? (
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <span className="text-sm text-gray-600">No detectadas</span>
                                    <button
                                        type="button"
                                        onClick={handleDetectTaxonomies}
                                        disabled={isDetectingTaxonomies}
                                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {isDetectingTaxonomies ? <Loading size="sm" /> : <Sparkles className="w-4 h-4" />}
                                        Detectar Taxonomías
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs text-gray-600">{taxonomiesConfig.filter((t: any) => t.enabled).length} habilitadas</span>
                                        <button
                                            type="button"
                                            onClick={handleDetectTaxonomies}
                                            disabled={isDetectingTaxonomies}
                                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                                            title="Actualizar taxonomías"
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 ${isDetectingTaxonomies ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {taxonomiesConfig.map((tax: any) => (
                                            <label
                                                key={tax.slug}
                                                className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={tax.enabled}
                                                    onChange={() => toggleTaxonomy(tax.slug)}
                                                    className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                                                />
                                                <div className="flex-1">
                                                    <span className="text-sm font-medium text-gray-900">{tax.label}</span>
                                                    <span className="ml-2 text-xs text-gray-400 font-mono">{tax.slug}</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                            {taxonomiesError && (
                                <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                                    <AlertCircle className="w-3.5 h-3.5" />
                                    {taxonomiesError}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Dominios Excluidos (Competidores) */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <Ban className="w-4 h-4 text-red-500" />
                            <h3 className="text-sm font-semibold text-gray-900">Dominios a Excluir de Referencias</h3>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Estos dominios no aparecerán como fuentes ni referencias en los artículos generados
                        </p>
                    </div>
                    <div className="p-5 space-y-3">
                        {/* Input para agregar dominio */}
                        <div className="flex gap-2">
                            <Input
                                value={newExcludedDomain}
                                onChange={(e) => setNewExcludedDomain(e.target.value)}
                                placeholder="competidor.com"
                                className="flex-1 text-sm bg-gray-50 border-gray-200"
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExcludedDomain())}
                            />
                            <button
                                type="button"
                                onClick={handleAddExcludedDomain}
                                disabled={!newExcludedDomain.trim()}
                                className="px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Lista de dominios */}
                        {excludedDomains.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-6 text-gray-400 text-xs">
                                <Ban className="w-8 h-8 mb-2 opacity-30" />
                                <p>Sin dominios excluidos</p>
                                <p className="text-[10px] mt-1">Los artículos podrán referenciar cualquier fuente</p>
                            </div>
                        ) : (
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {excludedDomains.map((item, idx) => (
                                    <div
                                        key={item.id}
                                        className="group flex items-center gap-2 p-2.5 bg-red-50 hover:bg-red-100 rounded-lg border border-red-100 transition-colors"
                                    >
                                        <span className="w-5 h-5 flex items-center justify-center text-[10px] font-medium text-red-400 bg-red-100 rounded">
                                            {idx + 1}
                                        </span>
                                        <span className="flex-1 text-sm text-red-700 font-mono truncate">
                                            {item.url}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteExcludedDomain(item.url)}
                                            className="p-1 text-red-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4">
                    {showDeleteConfirm ? (
                        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg">
                            <span className="text-sm text-red-700">¿Eliminar?</span>
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={isLoading}
                                className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                                Sí
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                            >
                                No
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            Eliminar
                        </button>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                        {isLoading ? <Loading size="sm" /> : <Save className="w-4 h-4" />}
                        Guardar
                    </button>
                </div>
            </form>
        </div>
    );
};



// Tipos para configuración de generación
interface GenerationConfig {
    topicCount: number;
    contentType: 'mix' | 'guides' | 'comparisons' | 'tutorials' | 'faqs' | 'trends' | 'listicles';
    includeWebscraping: boolean;
    useOpenAIAnalysis: boolean;
}

// Tipos para temas
interface TopicItem {
    id?: string;
    title: string;
    description: string;
    tema: string;
    descripcion: string;
    isDuplicate?: boolean;
    similarityScore?: number;
    generatedAt?: string;
}

const CONTENT_TYPE_LABELS: Record<string, string> = {
    mix: 'Variado (Recomendado)',
    guides: 'Guías completas',
    comparisons: 'Comparativas',
    tutorials: 'Tutoriales',
    faqs: 'Preguntas frecuentes',
    trends: 'Tendencias',
    listicles: 'Listas/Rankings'
};

export const TopicSuggestions: React.FC<{
    projectId: string;
    project: Project;
    onUpdate: (data: Partial<Project>) => Promise<void>;
}> = ({ projectId, project }) => {
    const [activeTab, setActiveTab] = useState<'competitors' | 'keywords' | 'negatives' | 'config'>('competitors');
    const [competitorUrls, setCompetitorUrls] = useState<{ id: string; url: string }[]>([]);
    const [newUrl, setNewUrl] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [results, setResults] = useState<TopicItem[] | null>(null);
    const [selectedTopics, setSelectedTopics] = useState<TopicItem[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [savedTopicsIds, setSavedTopicsIds] = useState<Set<string>>(new Set());
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [bulkText, setBulkText] = useState('');
    const [isImportingSemrush, setIsImportingSemrush] = useState(false);
    const [negativeKeywords, setNegativeKeywords] = useState<Array<{ id: number; keyword: string; type: string }>>([]);
    const [newNegativeKeyword, setNewNegativeKeyword] = useState('');
    const [negativeKeywordType, setNegativeKeywordType] = useState<'word' | 'phrase' | 'topic'>('word');
    const [projectKeywords, setProjectKeywords] = useState<Array<{ id: number; keyword: string }>>([]);
    const [newProjectKeyword, setNewProjectKeyword] = useState('');

    // Nuevos estados para mejoras
    const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
    const [editedTitle, setEditedTitle] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    const [generationConfig, setGenerationConfig] = useState<GenerationConfig>({
        topicCount: 15,
        contentType: 'mix',
        includeWebscraping: true,
        useOpenAIAnalysis: false
    });
    const editInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (project && project.urlsTemas) {
            let initialUrls: { id: string; url: string }[] = [];
            if (Array.isArray(project.urlsTemas)) {
                initialUrls = project.urlsTemas.map((u: any) => {
                    if (typeof u === 'string') {
                        return { id: Date.now().toString() + Math.random(), url: u };
                    }
                    return u;
                });
                setCompetitorUrls(initialUrls);
            }
        } else if (project) {
            setCompetitorUrls([]);
        }
    }, [project.urlsTemas, projectId]);

    useEffect(() => {
        setResults(null);
        setSelectedTopics([]);
        setSavedTopicsIds(new Set());
        setNewUrl('');
        setBulkText('');
        setProjectKeywords([]);
        setNewProjectKeyword('');
    }, [projectId]);

    useEffect(() => {
        const loadNegativeKeywords = async () => {
            if (project?.id) {
                try {
                    const keywords = await webhookService.getNegativeKeywords(project.id);
                    setNegativeKeywords(keywords);
                } catch (error) { }
            }
        };
        loadNegativeKeywords();
    }, [project?.id]);

    useEffect(() => {
        const loadProjectKeywords = async () => {
            if (project?.id) {
                try {
                    const kws = await webhookService.getProjectKeywords(project.id);
                    setProjectKeywords(kws);
                } catch (error) { }
            }
        };
        loadProjectKeywords();
    }, [project?.id]);

    // Cargar temas existentes para detectar duplicados
    useEffect(() => {
        const loadExistingTopics = async () => {
            if (project?.id) {
                try {
                    const topics = await webhookService.getTopics(project.id);
                    // Marcar como guardados los que ya existen
                    const savedIds = new Set<string>(topics.map((t: any) => t.titulo || t.url || ''));
                    setSavedTopicsIds(savedIds);
                } catch (error) { }
            }
        };
        loadExistingTopics();
    }, [project?.id]);

    // Focus en input de edición
    useEffect(() => {
        if (editingTopicId && editInputRef.current) {
            editInputRef.current.focus();
        }
    }, [editingTopicId]);

    const addUrlsToProject = async (urls: string[]) => {
        if (urls.length === 0) return;
        try {
            await webhookService.addProjectCompetitors(project.id, urls);
        } catch (error) {
            alert('Error al guardar las URLs');
        }
    };

    const handleAddUrl = async () => {
        if (!newUrl.trim()) return;
        const urlToAdd = newUrl.trim();
        const updatedUrls = [...competitorUrls, { id: Date.now().toString(), url: urlToAdd }];
        setCompetitorUrls(updatedUrls);
        setNewUrl('');
        await addUrlsToProject([urlToAdd]);
    };

    const handleSemrushImport = () => {
        if (!bulkText.trim()) return;
        const lines = bulkText.split('\n');
        const urlsToImport: string[] = [];
        lines.forEach(line => {
            const match = line.match(/https?:\/\/[^\s,;"']+/);
            if (match) urlsToImport.push(match[0]);
        });
        if (urlsToImport.length > 0) {
            const uniqueUrls = urlsToImport.filter((u, index, self) =>
                self.indexOf(u) === index && !competitorUrls.some(existing => existing.url === u)
            );
            if (uniqueUrls.length > 0) {
                const newItems = uniqueUrls.map(u => ({ id: Date.now().toString() + Math.random(), url: u }));
                setCompetitorUrls(prev => [...prev, ...newItems]);
                addUrlsToProject(uniqueUrls);
                setBulkText('');
                setIsImportingSemrush(false);
            }
        }
    };

    const handleDeleteUrl = async (id: string) => {
        const urlToDelete = competitorUrls.find(u => u.id === id)?.url;
        if (!urlToDelete) return;
        const updatedUrls = competitorUrls.filter(u => u.id !== id);
        setCompetitorUrls(updatedUrls);
        try {
            await webhookService.deleteProjectCompetitor(project.id, urlToDelete);
        } catch (error) {
            setCompetitorUrls([...competitorUrls]);
        }
    };

    const handleAddNegativeKeyword = async () => {
        if (!newNegativeKeyword.trim()) return;
        try {
            const result = await webhookService.addNegativeKeywords(project.id, [
                { keyword: newNegativeKeyword.trim(), type: negativeKeywordType }
            ]);
            if (result.added > 0) {
                const keywords = await webhookService.getNegativeKeywords(project.id);
                setNegativeKeywords(keywords);
                setNewNegativeKeyword('');
            }
        } catch (error) {
            alert('Error al añadir');
        }
    };

    const handleDeleteNegativeKeyword = async (keyword: string) => {
        try {
            await webhookService.deleteNegativeKeyword(project.id, keyword);
            const keywords = await webhookService.getNegativeKeywords(project.id);
            setNegativeKeywords(keywords);
        } catch (error) { }
    };

    const handleMarkTopicAsNegative = async (topic: any) => {
        const topicTitle = topic.title || topic.tema || 'Sin título';
        try {
            const result = await webhookService.addNegativeKeywords(project.id, [
                { keyword: topicTitle, type: 'topic' }
            ]);
            if (result.added > 0) {
                const keywords = await webhookService.getNegativeKeywords(project.id);
                setNegativeKeywords(keywords);
            }
        } catch (error) { }
    };

    const handleAddProjectKeyword = async () => {
        if (!newProjectKeyword.trim()) return;
        try {
            const kwList = newProjectKeyword.split(/[\n,]/).map(k => k.trim()).filter(k => k);
            if (kwList.length === 0) return;
            const result = await webhookService.addProjectKeywords(project.id, kwList);
            if (result.added > 0) {
                const updated = await webhookService.getProjectKeywords(project.id);
                setProjectKeywords(updated);
                setNewProjectKeyword('');
            }
        } catch (error) { }
    };

    const handleDeleteProjectKeyword = async (keyword: string) => {
        try {
            await webhookService.deleteProjectKeyword(project.id, keyword);
            const updated = await webhookService.getProjectKeywords(project.id);
            setProjectKeywords(updated);
        } catch (error) { }
    };

    const handleGenerate = async () => {
        if (competitorUrls.length === 0 && projectKeywords.length === 0) {
            alert('Añade al menos una URL o keyword');
            return;
        }
        setIsGenerating(true);
        setSelectedTopics([]);
        setResults(null);
        try {
            let generationMode = activeTab;
            if (activeTab === 'negatives' || activeTab === 'config') {
                generationMode = competitorUrls.length > 0 ? 'competitors' : 'keywords';
            }
            const payload = {
                projectId: project.id,
                mode: generationMode,
                competitors: competitorUrls.map(u => u.url),
                keywords: projectKeywords.map(k => k.keyword),
                negativeKeywords: negativeKeywords.map(nk => nk.keyword),
                // Nueva configuración
                topicCount: generationConfig.topicCount,
                contentType: generationConfig.contentType,
                includeWebscraping: generationConfig.includeWebscraping,
                useOpenAIAnalysis: generationConfig.useOpenAIAnalysis,
                language: (project as any).locale || 'es',
                timestamp: new Date().toISOString()
            };
            const response = await webhookService.generateContentStrategy(payload);
            setResults(response);
        } catch (error: any) {
            if (error.message === 'CANCELLED') {
                // Usuario canceló, no mostrar error
            } else {
                alert('Error al generar');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCancelGeneration = () => {
        apiCancelTokens.cancelTopicGeneration();
        setIsGenerating(false);
    };

    // Funciones de edición inline
    const startEditing = (topic: TopicItem) => {
        const topicId = topic.id || topic.title || topic.tema;
        setEditingTopicId(topicId);
        setEditedTitle(topic.title || topic.tema || '');
        setEditedDescription(topic.description || topic.descripcion || '');
    };

    const saveEdit = (topic: TopicItem) => {
        if (!results) return;
        const topicId = topic.id || topic.title || topic.tema;
        const updatedResults = results.map((t: TopicItem) => {
            const tId = t.id || t.title || t.tema;
            if (tId === topicId) {
                return {
                    ...t,
                    title: editedTitle,
                    tema: editedTitle,
                    description: editedDescription,
                    descripcion: editedDescription
                };
            }
            return t;
        });
        setResults(updatedResults);
        // También actualizar en selectedTopics si está seleccionado
        setSelectedTopics(prev => prev.map(t => {
            const tId = t.id || t.title || t.tema;
            if (tId === topicId) {
                return { ...t, title: editedTitle, tema: editedTitle, description: editedDescription, descripcion: editedDescription };
            }
            return t;
        }));
        setEditingTopicId(null);
    };

    const cancelEdit = () => {
        setEditingTopicId(null);
        setEditedTitle('');
        setEditedDescription('');
    };

    // Seleccionar/deseleccionar todos
    const selectAllTopics = () => {
        if (!results) return;
        const selectableTopics = results.filter((t: TopicItem) => {
            const topicId = t.title || t.tema;
            const isNegative = negativeKeywords.some(nk => nk.keyword === topicId);
            return !savedTopicsIds.has(topicId) && !isNegative;
        });
        setSelectedTopics(selectableTopics);
    };

    const deselectAllTopics = () => {
        setSelectedTopics([]);
    };

    const toggleTopicSelection = (topic: any) => {
        const topicId = topic.title || topic.tema;
        if (savedTopicsIds.has(topicId)) return;
        const isSelected = selectedTopics.some(t => (t.title || t.tema) === topicId);
        if (isSelected) {
            setSelectedTopics(selectedTopics.filter(t => (t.title || t.tema) !== topicId));
        } else {
            setSelectedTopics([...selectedTopics, topic]);
        }
    };

    const handleSaveSelected = async () => {
        if (selectedTopics.length === 0) return;
        setIsSaving(true);
        try {
            await webhookService.saveTopics(project.id, selectedTopics);
            const newSavedIds = new Set(savedTopicsIds);
            selectedTopics.forEach(t => newSavedIds.add(t.title || t.tema));
            setSavedTopicsIds(newSavedIds);
            setSuccessMessage(`${selectedTopics.length} temas guardados`);
            setShowSuccessModal(true);
            setSelectedTopics([]);
        } catch (error) {
            alert('Error al guardar');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)] gap-4 p-4">
            {/* Success Modal */}
            <Modal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Confirmación"
                variant="default"
                footer={<Button onClick={() => setShowSuccessModal(false)}>OK</Button>}
            >
                <div className="flex flex-col items-center p-4 text-center">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    </div>
                    <p className="text-gray-700">{successMessage}</p>
                </div>
            </Modal>

            {/* Left Panel */}
            <div className="w-full lg:w-[40%] flex flex-col gap-3 h-full">

                {/* Tab Selector */}
                <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
                    {[
                        { id: 'competitors', label: 'URLs', icon: Globe, count: competitorUrls.length },
                        { id: 'keywords', label: 'Keywords', icon: Hash, count: projectKeywords.length },
                        { id: 'negatives', label: 'Negativos', icon: X, count: negativeKeywords.length },
                        { id: 'config', label: 'Config', icon: Settings2, count: 0 },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-all ${
                                activeTab === tab.id
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <tab.icon className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{tab.label}</span>
                            {tab.count > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-gray-200 text-gray-600 rounded">
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Content Area */}
                <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {activeTab === 'competitors' ? (
                        <>
                            <div className="p-3 border-b border-gray-100">
                                <div className="flex gap-2 mb-2">
                                    <Input
                                        value={newUrl}
                                        onChange={(e) => setNewUrl(e.target.value)}
                                        placeholder="https://competencia.com"
                                        className="flex-1 text-sm bg-gray-50 border-gray-200"
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
                                    />
                                    <button
                                        onClick={handleAddUrl}
                                        disabled={!newUrl}
                                        className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                                <button
                                    onClick={() => setIsImportingSemrush(!isImportingSemrush)}
                                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                                >
                                    <Download className="w-3 h-3" />
                                    Importar lista
                                </button>
                                {isImportingSemrush && (
                                    <div className="mt-2">
                                        <Textarea
                                            value={bulkText}
                                            onChange={(e) => setBulkText(e.target.value)}
                                            placeholder="Pega URLs (una por línea)"
                                            rows={3}
                                            className="text-xs bg-gray-50"
                                        />
                                        <button
                                            onClick={handleSemrushImport}
                                            className="mt-2 w-full py-2 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                        >
                                            Procesar
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                {competitorUrls.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs">
                                        <Globe className="w-8 h-8 mb-2 opacity-30" />
                                        Añade URLs
                                    </div>
                                ) : (
                                    competitorUrls.map((item, idx) => (
                                        <div key={item.id} className="group flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                                            <span className="w-5 h-5 flex items-center justify-center text-[10px] font-medium text-gray-400 bg-gray-100 rounded">
                                                {idx + 1}
                                            </span>
                                            <span className="flex-1 text-xs text-gray-600 truncate font-mono">
                                                {item.url.replace(/https?:\/\//, '').split('/')[0]}
                                            </span>
                                            <button
                                                onClick={() => handleDeleteUrl(item.id)}
                                                className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    ) : activeTab === 'keywords' ? (
                        <>
                            <div className="p-3 border-b border-gray-100">
                                <div className="flex gap-2">
                                    <Input
                                        value={newProjectKeyword}
                                        onChange={(e) => setNewProjectKeyword(e.target.value)}
                                        placeholder="Añadir keyword"
                                        className="flex-1 text-sm bg-gray-50 border-gray-200"
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddProjectKeyword()}
                                    />
                                    <button
                                        onClick={handleAddProjectKeyword}
                                        className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                {projectKeywords.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs">
                                        <Hash className="w-8 h-8 mb-2 opacity-30" />
                                        Sin keywords
                                    </div>
                                ) : (
                                    projectKeywords.map((item, idx) => (
                                        <div key={item.id || idx} className="group flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                                            <span className="w-5 h-5 flex items-center justify-center text-[10px] font-medium text-gray-400 bg-gray-100 rounded">
                                                {idx + 1}
                                            </span>
                                            <span className="flex-1 text-sm text-gray-700">{item.keyword}</span>
                                            <button
                                                onClick={() => handleDeleteProjectKeyword(item.keyword)}
                                                className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    ) : activeTab === 'negatives' ? (
                        <>
                            <div className="p-3 border-b border-gray-100">
                                <div className="flex gap-2">
                                    <select
                                        value={negativeKeywordType}
                                        onChange={(e) => setNegativeKeywordType(e.target.value as any)}
                                        className="px-2 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50"
                                    >
                                        <option value="word">Palabra</option>
                                        <option value="phrase">Frase</option>
                                        <option value="topic">Tema</option>
                                    </select>
                                    <Input
                                        value={newNegativeKeyword}
                                        onChange={(e) => setNewNegativeKeyword(e.target.value)}
                                        placeholder="Añadir negativo"
                                        className="flex-1 text-sm bg-gray-50 border-gray-200"
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddNegativeKeyword()}
                                    />
                                    <button
                                        onClick={handleAddNegativeKeyword}
                                        className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                {negativeKeywords.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs">
                                        <X className="w-8 h-8 mb-2 opacity-30" />
                                        Sin negativos
                                    </div>
                                ) : (
                                    negativeKeywords.map((item, idx) => (
                                        <div key={item.id} className="group flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                                            <span className="w-5 h-5 flex items-center justify-center text-[10px] font-medium text-gray-400 bg-gray-100 rounded">
                                                {idx + 1}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-sm text-gray-700 block truncate">{item.keyword}</span>
                                                <span className="text-[10px] text-gray-400 uppercase">{item.type}</span>
                                            </div>
                                            <button
                                                onClick={() => handleDeleteNegativeKeyword(item.keyword)}
                                                className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    ) : (
                        /* Config Tab */
                        <div className="p-4 space-y-4 overflow-y-auto">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">Cantidad de temas</label>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="range"
                                        min="5"
                                        max="30"
                                        step="5"
                                        value={generationConfig.topicCount}
                                        onChange={(e) => setGenerationConfig(prev => ({ ...prev, topicCount: parseInt(e.target.value) }))}
                                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                                    />
                                    <span className="w-8 text-center text-sm font-medium text-gray-900">{generationConfig.topicCount}</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">Tipo de contenido</label>
                                <select
                                    value={generationConfig.contentType}
                                    onChange={(e) => setGenerationConfig(prev => ({ ...prev, contentType: e.target.value as any }))}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white"
                                >
                                    {Object.entries(CONTENT_TYPE_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-3 pt-2">
                                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={generationConfig.includeWebscraping}
                                        onChange={(e) => setGenerationConfig(prev => ({ ...prev, includeWebscraping: e.target.checked }))}
                                        className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <FileSearch className="w-4 h-4 text-gray-500" />
                                            <span className="text-sm font-medium text-gray-900">Webscraping de competidores</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">Extrae contenido de URLs para análisis más profundo</p>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={generationConfig.useOpenAIAnalysis}
                                        onChange={(e) => setGenerationConfig(prev => ({ ...prev, useOpenAIAnalysis: e.target.checked }))}
                                        className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <Brain className="w-4 h-4 text-gray-500" />
                                            <span className="text-sm font-medium text-gray-900">Análisis con OpenAI</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">Usa GPT para detectar gaps de contenido (costo adicional)</p>
                                    </div>
                                </label>
                            </div>

                            <div className="pt-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                                <div className="flex items-start gap-2">
                                    <Zap className="w-4 h-4 text-amber-600 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-medium text-amber-800">Costo estimado</p>
                                        <p className="text-xs text-amber-700 mt-0.5">
                                            ~$0.05-0.15 por generación
                                            {generationConfig.useOpenAIAnalysis && ' (+$0.01 OpenAI)'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Generate Button */}
                {isGenerating ? (
                    <button
                        onClick={handleCancelGeneration}
                        className="w-full py-3 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 flex items-center justify-center gap-2"
                    >
                        <StopCircle className="w-4 h-4" /> Cancelar
                    </button>
                ) : (
                    <button
                        onClick={handleGenerate}
                        disabled={competitorUrls.length === 0 && projectKeywords.length === 0}
                        className="w-full py-3 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        <Sparkles className="w-4 h-4" /> Generar {generationConfig.topicCount} Temas
                    </button>
                )}
            </div>

            {/* Right Panel - Results */}
            <div className="w-full lg:w-[60%] h-full">
                <div className="h-full bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                                <Lightbulb className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Resultados</h3>
                                <p className="text-xs text-gray-500">
                                    {results && Array.isArray(results) ? `${results.length} temas generados` : 'Genera ideas de contenido'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {results && Array.isArray(results) && results.length > 0 && (
                                <>
                                    <button
                                        onClick={selectedTopics.length > 0 ? deselectAllTopics : selectAllTopics}
                                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                                    >
                                        {selectedTopics.length > 0 ? 'Deseleccionar' : 'Seleccionar todos'}
                                    </button>
                                </>
                            )}
                            {selectedTopics.length > 0 && (
                                <button
                                    onClick={handleSaveSelected}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                                >
                                    {isSaving ? <Loading size="sm" /> : <Save className="w-4 h-4" />}
                                    Guardar ({selectedTopics.length})
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 p-4 overflow-y-auto">
                        {!results && !isGenerating ? (
                            <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                                <Target className="w-12 h-12 mb-4 opacity-30" />
                                <p className="text-sm">Configura y genera tu estrategia</p>
                                <p className="text-xs mt-2 max-w-xs">Añade URLs de competidores o keywords y ajusta la configuración para generar ideas de contenido</p>
                            </div>
                        ) : isGenerating ? (
                            <div className="h-full flex flex-col items-center justify-center">
                                <Loading size="lg" />
                                <p className="mt-4 text-sm text-gray-500">
                                    {generationConfig.includeWebscraping ? 'Analizando competidores...' : 'Generando ideas...'}
                                </p>
                                <p className="mt-2 text-xs text-gray-400">Esto puede tardar 30-60 segundos</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {(() => {
                                    if (!results) return null;
                                    const dataList = Array.isArray(results) ? results : ((results as any).temas || (results as any).ideas || []);
                                    if (Array.isArray(dataList) && dataList.length > 0) {
                                        return dataList.map((item: TopicItem, idx: number) => {
                                            const topicId = item.id || item.title || item.tema;
                                            const topicTitle = item.title || item.tema;
                                            const isSaved = savedTopicsIds.has(topicTitle);
                                            const isSelected = selectedTopics.some(t => (t.title || t.tema) === topicTitle);
                                            const isNegative = negativeKeywords.some(nk => nk.keyword === topicTitle);
                                            const isDuplicate = item.isDuplicate || (item.similarityScore && item.similarityScore > 0.7);
                                            const isEditing = editingTopicId === topicId;

                                            return (
                                                <div
                                                    key={topicId || idx}
                                                    onClick={() => !isNegative && !isEditing && toggleTopicSelection(item)}
                                                    className={`p-4 rounded-xl border transition-all ${
                                                        isEditing
                                                            ? 'bg-blue-50 border-blue-300'
                                                            : isSaved
                                                            ? 'bg-emerald-50 border-emerald-200 cursor-default'
                                                            : isNegative
                                                            ? 'bg-red-50 border-red-200 opacity-60 cursor-default'
                                                            : isDuplicate
                                                            ? 'bg-amber-50 border-amber-200 cursor-pointer'
                                                            : isSelected
                                                            ? 'bg-gray-900 border-gray-900 text-white cursor-pointer'
                                                            : 'bg-white border-gray-200 hover:border-gray-300 cursor-pointer'
                                                    }`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                                                            isSaved ? 'bg-emerald-200 text-emerald-700' :
                                                            isNegative ? 'bg-red-200 text-red-700' :
                                                            isDuplicate ? 'bg-amber-200 text-amber-700' :
                                                            isSelected ? 'bg-white text-gray-900' :
                                                            'bg-gray-100 text-gray-500'
                                                        }`}>
                                                            {idx + 1}
                                                        </span>
                                                        <div className="flex-1 min-w-0">
                                                            {isEditing ? (
                                                                <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                                                                    <input
                                                                        ref={editInputRef}
                                                                        type="text"
                                                                        value={editedTitle}
                                                                        onChange={(e) => setEditedTitle(e.target.value)}
                                                                        className="w-full px-2 py-1 text-sm font-medium border border-blue-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                        placeholder="Título del tema"
                                                                    />
                                                                    <textarea
                                                                        value={editedDescription}
                                                                        onChange={(e) => setEditedDescription(e.target.value)}
                                                                        className="w-full px-2 py-1 text-xs border border-blue-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                                                        rows={2}
                                                                        placeholder="Descripción"
                                                                    />
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            onClick={() => saveEdit(item)}
                                                                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                                                        >
                                                                            <Check className="w-3 h-3" /> Guardar
                                                                        </button>
                                                                        <button
                                                                            onClick={cancelEdit}
                                                                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                                                                        >
                                                                            <XCircle className="w-3 h-3" /> Cancelar
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <h4 className={`font-medium text-sm ${isSelected && !isSaved && !isNegative ? 'text-white' : ''}`}>
                                                                            {item.tema || item.title}
                                                                            {isSaved && <span className="ml-2 text-xs text-emerald-600">✓ Guardado</span>}
                                                                            {isDuplicate && !isSaved && (
                                                                                <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-600">
                                                                                    <AlertTriangle className="w-3 h-3" /> Similar existente
                                                                                </span>
                                                                            )}
                                                                        </h4>
                                                                        {!isSaved && !isNegative && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    startEditing(item);
                                                                                }}
                                                                                className={`p-1 rounded ${isSelected ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                                                                                title="Editar"
                                                                            >
                                                                                <Pencil className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    <p className={`text-xs mt-1 ${isSelected && !isSaved && !isNegative ? 'text-gray-300' : 'text-gray-500'}`}>
                                                                        {item.descripcion || item.description}
                                                                    </p>
                                                                    {!isSaved && !isNegative && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleMarkTopicAsNegative(item);
                                                                            }}
                                                                            className={`mt-2 text-xs ${isSelected ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-red-500'}`}
                                                                        >
                                                                            Marcar negativo
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    }
                                    return <pre className="text-xs bg-gray-100 p-4 rounded-lg overflow-auto">{JSON.stringify(results, null, 2)}</pre>;
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
